import { rgba } from 'polished'
import * as R from 'ramda'

import { resolveSelfRef } from './utils/configUtils.js'
import './ui/_fonts.css'

const defaultPalette = {
  primary: '#2266DD',
  primaryButton: '#2266DD',
  primaryButtonText: '#FFFFFF',
  primaryButtonHover: '#124CB1',
  secondary: '#999999',
  secondaryButton: '#EBEBEB',
  secondaryButtonText: '#2266DD',
  secondaryButtonHover: '#D9D9D9',
  errorState: '#E52727',
  primaryText: '#333333',
  secondaryText: '#666666',
  background: '#FFFFFF',
  hoverClickStateBase: '>>primaryText',

  toolTipBackground: '#000',
  toolTipText: '#FFFFFF',

  mapNavBackground: '#333333',
  mapNavText: '#FFFFFF',
  mapNavSearchResults: '#CA4800',
  mapNavSearchResultsText: '#FFFFFF',

  widgetBackground: '#F9F9F9',
  widgetIconFill: '#333333',
  widgetText: '#333333',

  statusOpenBackground: '#1F8845',
  statusOpenText: '#FFFFFF',
  statusClosedBackground: '#ebebeb',
  statusClosedText: '#333333',
  statusEarlyBackground: '#057DBC',
  statusEarlyText: '#FFFFFF',
  statusDelayedBackground: '#CA4800',
  statusDelayedText: '#FFFFFF',
  statusCancelledBackground: '#E52727',
  statusCancelledText: '#FFFFFF',
  statusSuccessBackground: '#229653',
  statusSuccessText: '#FFFFFF',
  statusErrorBackground: '#1F8845',
  statusErrorText: '#FFFFFF',

  adminMarkerText: '#4d4563',
  adminMarkerStroke: '#FFFFFF',
  programmaticPinMarkersText: '#4d4563',
  programmaticPinMarkersStroke: '#FFFFFF',

  keyboard: {
    get background () { return '#D2D5DA' },
    get buttonBackground () { return '#FFFFFF' },
    get buttonPrimary () { return '#6D6D6D' },
    get actionButtonBackground () { return '#D0D0D0' },
    get actionButtonColor () { return '#6D6D6D' },
    get submitButtonBackground () { return '#A2A2A2' },
    get submitButtonColor () { return '#FFFFFF' },
    get activeButtonBackground () { return '#AEB4BD' },
    get activeButtonColor () { return '#FFFFFF' }
  }
}

const defaultFonts = {
  family: `'Roboto','Helvetica','Arial',sans-serif`,
  size: {
    h1: '24px',
    h2: '18px',
    h3: '16px',
    h4: '14px',
    h5: '12px',
    h6: '10px'
  }
}

const defaultHeader = {
  padding: '20px',
  paddingMobile: '12px 10px',
  paddingWithLogo: '20px',
  paddingWithLogoMobile: '18px 12px 17px 20px',
  hasLogo: true
}

const defaultLogo = {
  height: '28px',
  padding: '0',
  margin: '0',
  centered: false
}

const fontWeights = {
  lighter: 400,
  normal: 500,
  bold: 700
}

const defaultTheme = {
  colors: defaultPalette,
  fonts: defaultFonts,
  fontWeights,
  header: defaultHeader,
  logo: defaultLogo,
  globalRadius: 4,
  standardRadius: '8px'
  // The above globalRadius value has been exposed to customers for theming, but we have not used it consistenly
  // enough to match current Figma spec. So standardRadius has been introduced and used to implement
  // current design spec where all border radii match. Going forward, use standardRadius.
}

const getTheme = async (customTheme, defaultThemeName) => {
  let DStheme = null
  if (defaultThemeName === 'mod') {
    DStheme = (await import('@locus-labs/mod-default-theme')).default
  } else {
    DStheme = (await import('@locus-labs/mol-desktop-default-theme')).default
  }
  const defaultDStheme = R.mergeDeepRight(defaultTheme, DStheme) // merge default theme with Design Systems theme
  const theme = R.mergeDeepRight(defaultDStheme, customTheme) // apply client-specific theme values

  const colors = Object.assign({}, theme.colors)
  const header = Object.assign({}, theme.header)
  const logo = Object.assign({}, theme.logo)
  if (logo.height)
    logo.height = parseInt(logo.height) // convert strings like "28px" to numbers, which is what the Logo component expects

  theme.logo = logo

  colors.navBarButtonBackground = colors.navBarButtonBackground || colors.primaryButtonText
  colors.navBarButtonText = colors.navBarButtonText || colors.primaryButton

  theme.colors = resolveSelfRef(colors)

  theme.colors.hoverClickState = theme.colors.hoverClickState || rgba(theme.colors.hoverClickStateBase, 0.1)

  header.paddingWithLogo = header.paddingWithLogo || header.padding
  header.paddingWithLogoMobile = header.paddingWithLogoMobile || header.paddingMobile
  theme.header = header

  return {
    ...theme,
    borderRadius: (radius) => `border-radius: ${radius}px; background-clip: padding-box;`,
    boxShadow: (horiz = 0, vert = 0, blur = '8px', spread = '0px', color = rgba(0, 0, 0, 0.25)) =>
      `box-shadow: ${horiz} ${vert} ${blur} ${spread} ${color};`,
    fontSize: (size) => `font-size: ${size.startsWith('h') ? theme.fonts.size[size] : size};`
  }
}

export { getTheme }
